import React from 'react';
import { Container, Box, CircularProgress, Typography, Grid, Paper } from '@mui/material';
import StatCard from './Statistics/StatCard';
import EmotionDistribution from './Statistics/EmotionDistribution';
import WeekdayImpact from './Statistics/WeekdayImpact';
import Progress from './Statistics/Progress';
import BasicStats from './Statistics/BasicStats';
import SerenityTrends from './Statistics/SerenityTrends';
import TopKeywords from './Statistics/TopKeywords';
import TopAnalyses from './Statistics/TopAnalyses';
import EmotionThemeTreemap from './Statistics/EmotionThemeTreemap';
import { useStatistics } from '../hooks/useStatistics';
import { useEmotionThemeCorrelation } from '../hooks/useEmotionThemeCorrelation';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from './SEO/SEO';

const Statistics: React.FC = () => {
  const { currentUser } = useAuth();
  const { loading, error, data } = useStatistics();
  const correlationData = data && data.dreams ? useEmotionThemeCorrelation(data.dreams, 30) : [];
  const { t } = useTranslation();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ 
        mt: 4,
        mb: 4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 100px)'
      }}>
        <CircularProgress sx={{ color: '#FF4B8C' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ 
        mt: 4,
        mb: 4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 100px)'
      }}>
        <Typography color="error">
          Une erreur est survenue lors du chargement des statistiques.
        </Typography>
      </Container>
    );
  }

  const { 
    dreamFrequencyData, 
    emotionData, 
    keywordsData, 
    weekdayData, 
    progressData,
    analysesData 
  } = data;

  return (
  <>
    <SEO 
      title={t('seo.statistics.title', 'Statistiques et Analyse | LunaNüva')}
      description={t('seo.statistics.description', 'Visualisez l\'\u00e9volution de vos rêves, identifiez les motifs récurrents et suivez votre progression.')}
      keywords={t('seo.statistics.keywords', 'statistiques rêves, analyse rêves, motifs rêves, progression onirique')}
    />
    <Box sx={{background: 'var(--Pages-background4)', minHeight: '100vh', display: 'flex',flexDirection: 'column'}}>
    <Container maxWidth="lg" sx={{ mt: 0, mb: 8 }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mt: { xs: 9, sm: 11, md: 12 },
        mb: { xs: 6, sm: 7, md: 9 }
      }}>
        {/* Conteneur Grid centré : illustration + texte */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0, // Pas d'espacement par défaut pour contrôler le chevauchement
          mt: 0,
          width: '100%'
        }}>
          {/* Illustration à gauche */}
          <Box 
            component="img"
            src="/assets/home/statistics2.png"
            alt="Statistics"
            sx={{
              maxWidth: { xs: '120px', sm: '170px', md: '200px' },
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          
          {/* Texte à droite avec chevauchement */}
          <Box sx={{
            fontFamily: '"Amatic SC", cursive',
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
            fontWeight: { xs: '600', sm: '400', md: '400' },
            color: 'var(--ConnectButton)',            
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            justifySelf: 'start',
            marginLeft: { xs: '-80px', sm: '-85px', md: '-90px' }, // Marge négative pour chevauchement
            zIndex: 2 // Au-dessus de l'illustration
          }}>
            {/* Ligne 1: Découvrir */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-3deg)',
              transformOrigin: 'left center',
              ml: 10,
              mb: -1
            }}>
              {t('statistics.headerSubtitle').split(' ')[0]}
            </Box>
            
            {/* Ligne 2: mes tendances */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(3deg)',
              transformOrigin: 'left center',
              ml: 5,
              mb: 0
            }}>
              {t('statistics.headerSubtitle').split(' ')[1]} {t('statistics.headerSubtitle').split(' ')[2]}
            </Box>
            
            {/* Ligne 3: oniriques */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-2deg)',
              transformOrigin: 'left center',
              ml: 10
            }}>
              {t('statistics.headerSubtitle').split(' ')[3]}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* StatCards sur toute la largeur */}
      <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1, mb: 2, mr: 2 }}>
        <StatCard title="" sx={{background: 'var(--stat-progress)'}}>             
          <Typography variant="h6" sx={{ 
            color: 'var(--light-green)',
            mb: 1,
            opacity: 0.8
            }}>
            {t('statistics.progress.dreams')}
          </Typography>
          <Progress data={progressData} type="dreams" />
        </StatCard>
        <StatCard title="" sx={{background: 'var(--stat-exploits)'}}>
          <Typography variant="h6" sx={{ 
              color: 'var(--light-green)',
              mb: 1,
              opacity: 0.8
              }}>
              {t('statistics.progress.exploits')}
            </Typography>
          <Progress data={progressData} type="categories" />
        </StatCard>
      </Box>

      {/* Statistiques de base sans carte */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h6" sx={{textAlign: 'center', color: '#68f1d1', mb: 2 }}>
          {t('statistics.basicStats.title')}
        </Typography>
        <BasicStats data={dreamFrequencyData} />
      </Box>

         {/* Tendances de sérénité sur une ligne */}
         <Box sx={{
        textAlign: 'center',
        display: 'flex',
        gap: 4,        
        mb: 4,
        flexWrap: 'wrap',
        '& > *': {
          flex: '1 1 300px',
          minWidth: '300px'
        }
      }}>
        <StatCard title={t('statistics.serenityTrends.lastWeek')} >
          <SerenityTrends data={dreamFrequencyData} period={7} />
        </StatCard>
        <StatCard title={t('statistics.serenityTrends.lastMonth')} >
          <SerenityTrends data={dreamFrequencyData} period={30} />
        </StatCard>
      </Box>

      {/* Ligne de cartes responsive */}
      <Box sx={{
        textAlign: 'center',
        display: 'flex',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
        '& > *': {
          flex: '1 1 300px',
          minWidth: '300px'
        }
      }}>
        <StatCard title={t('statistics.topKeywords.title')} sx={{ mb: 2 }}>
          <TopKeywords data={keywordsData} />
        </StatCard>
        <StatCard title={t('statistics.emotionDistribution.title')} sx={{ mb: 2 }}>
          <EmotionDistribution data={emotionData} />
        </StatCard>
        <StatCard title={t('statistics.topAnalyses.title')} sx={{ mb: 2 }}>
          <TopAnalyses data={analysesData} />
        </StatCard>
      </Box>

      {/* Impact par Jour sur toute la largeur */}
      <Box sx={{ textAlign: 'center', mr: 1, mb: 3 }}>
        <StatCard title={t('statistics.weekdayImpact.title')}>
          <WeekdayImpact data={weekdayData} />
        </StatCard>
      </Box>

      {/* Treemap des corrélations Émotions-Thèmes */}
      <Grid container spacing={3}>
        {correlationData.length > 0 ? (
          <>
            <Grid item xs={12} sm={6}>
              <StatCard title={t('statistics.emotionThemeTreemap.title') + ` - ${correlationData[0].emotion}`} sx={{ textAlign: 'center' }}>
                <EmotionThemeTreemap 
                  data={[correlationData[0]]} 
                  period={30}
                />
              </StatCard>
            </Grid>
            {correlationData.length > 1 && (
              <Grid item xs={12} sm={6}>
                <StatCard title={t('statistics.emotionThemeTreemap.title') + ` - ${correlationData[1].emotion}`} sx={{ textAlign: 'center' }}>
                  <EmotionThemeTreemap 
                    data={[correlationData[1]]} 
                    period={30}
                  />
                </StatCard>
              </Grid>
            )}
          </>
        ) : null}
      </Grid>
    </Container>
  </Box>
  </>
  );
};

export default Statistics;
